/**
 * SkinQuiz — Unified skin assessment quiz (full-page)
 * Used by both the public SkinAnalysisWizard page and the member portal NewAssessmentPage.
 *
 * Cross-tab session awareness: listens for BroadcastChannel 'marple_auth' events and
 * storage key changes so it picks up a login that happened in another tab.
 *
 * Member mode  → creates assessment via member API → uploads photos → submits for llama3.2 → polls
 * Guest mode   → shows a preliminary summary → CTA to create account for full AI analysis
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Camera, Upload, CheckCircle2, RefreshCw, X, Sparkles,
  ChevronRight, ChevronLeft, AlertCircle, Smartphone, User,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { isAuthenticated } from '../services/memberApi';
import memberApi, { pollAIStatus } from '../services/memberApi';
import { ScanningStage } from './skin/ScanningStage';
import { SkinReadingResults } from './skin/SkinReadingResults';
import type { AIAnalysisResult } from '../types/member';

// ─── Brand ────────────────────────────────────────────────────────────────────
const C = {
  green:  '#567D38',
  lime:   '#BDD630',
  sand:   '#F7F5EE',
  ink:    '#1A2E12',
  stone:  '#6B7B6E',
  clay:   '#D9B38C',
};

// ─── Static data ──────────────────────────────────────────────────────────────
const SKIN_TYPES = [
  { id: 'normal',      label: 'Normal',      emoji: '✨', desc: 'Balanced, minimal concerns' },
  { id: 'dry',         label: 'Dry',         emoji: '🌵', desc: 'Tight, flaky or rough' },
  { id: 'oily',        label: 'Oily',        emoji: '💧', desc: 'Shiny, prone to breakouts' },
  { id: 'combination', label: 'Combination', emoji: '☯️', desc: 'Oily T-zone, drier cheeks' },
  { id: 'sensitive',   label: 'Sensitive',   emoji: '🌸', desc: 'Reactive, easily irritated' },
];

const CONCERNS = [
  'Acne & Breakouts', 'Blackheads', 'Dryness', 'Oiliness',
  'Redness', 'Dark Spots', 'Fine Lines', 'Uneven Texture',
  'Large Pores', 'Sensitivity', 'Dullness', 'Pigmentation',
  'Under-eye Circles', 'Dehydration',
];

const ROUTINES = [
  { id: 'none',         label: 'None',         desc: 'Soap & water only' },
  { id: 'basic',        label: 'Basic',        desc: 'Cleanser + moisturiser' },
  { id: 'moderate',     label: 'Moderate',     desc: 'Cleanser, toner, moisturiser' },
  { id: 'advanced',     label: 'Advanced',     desc: 'Full routine with serums' },
  { id: 'prescription', label: 'Prescription', desc: 'Using prescribed skincare' },
];

const STEP_LABELS = ['Skin Type', 'Concerns', 'Lifestyle', 'Photos', 'Consent'];
const TOTAL_STEPS = 5;

// ─── Types ────────────────────────────────────────────────────────────────────
interface QuizData {
  skinType:    string;
  ageRange:    string;
  concerns:    string[];
  sensitivity: string;
  routine:     string;
  photos: {
    front: File | null;
    left:  File | null;
    right: File | null;
  };
  consent: {
    analysis:       boolean;
    photoRetention: boolean;
    marketing:      boolean;
  };
}

interface SessionState {
  loggedIn:  boolean;
  firstName: string;
}

export interface SkinQuizProps {
  /** Force member mode even without stored token (e.g. when navigating from portal) */
  forceMember?: boolean;
}

// ─── Main component ───────────────────────────────────────────────────────────
export function SkinQuiz({ forceMember }: SkinQuizProps) {
  const navigate = useNavigate();

  const [step, setStep]                   = useState(1);
  const [showQR, setShowQR]               = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [submitError, setSubmitError]     = useState<string | null>(null);
  const [assessmentId, setAssessmentId]   = useState<string | null>(null);
  const [jobStatus, setJobStatus]         = useState<string | null>(null);
  // The finished reading is rendered inline rather than bouncing the user to a
  // detail page — the redesign treats the result as the end of the same flow.
  const [reading, setReading]             = useState<AIAnalysisResult | null>(null);
  const [scanPhotoUrl, setScanPhotoUrl]   = useState<string | null>(null);

  // Object URLs must be released or the blob stays resident for the session.
  useEffect(() => {
    if (!scanPhotoUrl) return;
    return () => URL.revokeObjectURL(scanPhotoUrl);
  }, [scanPhotoUrl]);

  const [session, setSession] = useState<SessionState>({
    loggedIn:  forceMember ?? isAuthenticated(),
    firstName: '',
  });

  const [data, setData] = useState<QuizData>({
    skinType:    '',
    ageRange:    '',
    concerns:    [],
    sensitivity: 'medium',
    routine:     'basic',
    photos:      { front: null, left: null, right: null },
    consent:     { analysis: false, photoRetention: false, marketing: false },
  });

  // ── Cross-tab session sync ─────────────────────────────────────────────────
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('marple_auth');
      channel.onmessage = (e) => {
        if (e.data?.type === 'LOGIN') {
          setSession({ loggedIn: true, firstName: e.data.firstName || '' });
        } else if (e.data?.type === 'LOGOUT') {
          setSession({ loggedIn: false, firstName: '' });
        }
      };
    } catch { /* BroadcastChannel not available */ }

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'marple_member_token') {
        setSession(prev => ({ ...prev, loggedIn: !!e.newValue }));
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      channel?.close();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ── State helpers ─────────────────────────────────────────────────────────
  const update = useCallback(<K extends keyof QuizData>(key: K, val: QuizData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
  }, []);

  const toggleConcern = (c: string) => {
    setData(prev => ({
      ...prev,
      concerns: prev.concerns.includes(c)
        ? prev.concerns.filter(x => x !== c)
        : [...prev.concerns, c],
    }));
  };

  const canProceed = () => {
    if (step === 1) return !!data.skinType;
    if (step === 4) return !!(data.photos.front || data.photos.left || data.photos.right);
    if (step === 5) return data.consent.analysis;
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      if (session.loggedIn) {
        // ── Member flow ──────────────────────────────────────────────────────
        const questionnaire = [
          { question_id: 'skin_type',   question_text: 'What is your skin type?',          response: data.skinType },
          { question_id: 'age_range',   question_text: 'What is your age range?',           response: data.ageRange || 'not specified' },
          { question_id: 'concerns',    question_text: 'What are your skin concerns?',      response: data.concerns.join(', ') },
          { question_id: 'sensitivity', question_text: 'What is your sensitivity level?',   response: data.sensitivity },
          { question_id: 'routine',     question_text: 'What is your current routine?',     response: data.routine },
        ];

        const assessment = await memberApi.assessments.createAssessment({
          assessment_type:         'comprehensive',
          questionnaire_responses: questionnaire,
        } as any);

        // Upload photos
        const photoEntries = Object.entries(data.photos).filter(([, f]) => f !== null);
        console.log(`[SkinQuiz] Uploading ${photoEntries.length} photo(s) to assessment ${assessment.id}`);
        for (const [type, file] of photoEntries) {
          console.log(`[SkinQuiz] Uploading ${type} image: ${(file as File).name} (${(file as File).size} bytes)`);
          await memberApi.assessments.uploadImage(assessment.id, type, file as File);
          console.log(`[SkinQuiz] ${type} image uploaded successfully`);
        }

        await memberApi.assessments.submitAssessment(assessment.id);
        setAssessmentId(assessment.id);
        setJobStatus('queued');

        // Poll for result (navigates automatically on completion)
        // Show the front photo under the scan animation, if one was supplied.
        const front = data.photos.front;
        if (front) setScanPhotoUrl(URL.createObjectURL(front as File));

        const stopPoll = pollAIStatus(
          assessment.id,
          (s) => setJobStatus(s.status),
          async () => {
            stopPoll();
            try {
              const aiResult = await memberApi.assessments.getAIResult(assessment.id);
              setReading(aiResult);
            } catch {
              // The reading completed but we could not fetch it — send the user
              // to the detail page rather than stranding them on a spinner.
              navigate(`/member/assessments/${assessment.id}`);
            }
          },
          (err) => { stopPoll(); setSubmitError(err.message); },
        );

      } else {
        // ── Guest flow ───────────────────────────────────────────────────────
        setAssessmentId('guest');
        setJobStatus('guest');
      }
    } catch (err) {
      let msg = err instanceof Error ? err.message : String(err);
      // Pydantic validation returns detail as an array — flatten to a readable string
      if (typeof msg === 'object' || msg.startsWith('[object')) {
        msg = 'Submission failed. Please try again.';
      }
      const lower = msg.toLowerCase();
      if (lower.includes('token') && (lower.includes('expired') || lower.includes('invalid'))) {
        // Clear stale session so user can re-login
        localStorage.removeItem('marple_member_token');
        setSession({ loggedIn: false, firstName: '' });
        setSubmitError('Your session stepped away. Sign back in — your ritual is exactly where you left it.');
      } else if (lower.includes('401') || lower.includes('authentication') || lower.includes('unauthorized')) {
        setSubmitError('Your glow pass needs a refresh. Sign back in to continue.');
      } else if (lower.includes('404') || lower.includes('not found')) {
        setSubmitError('This path leads somewhere new. Let\'s get you back on your ritual route.');
      } else if (lower.includes('network') || lower.includes('fetch') || lower.includes('failed to fetch')) {
        setSubmitError('The signal got lost in the garden. Check your connection and try again.');
      } else if (lower.includes('500') || lower.includes('internal')) {
        setSubmitError('Your skin had a moment — let\'s reset. Please try again.');
      } else {
        setSubmitError(msg || 'Your skin had a moment — let\'s reset. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // ── Post-submit screens ───────────────────────────────────────────────────
  if (reading) {
    return (
      <SkinReadingResults
        result={reading}
        onRetake={() => {
          setReading(null);
          setAssessmentId(null);
          setJobStatus(null);
          setScanPhotoUrl(null);
          setStep(1);
        }}
      />
    );
  }
  if (assessmentId && session.loggedIn) {
    return (
      <ScanningStage
        jobStatus={jobStatus}
        photoUrl={scanPhotoUrl}
        error={submitError}
        onRetry={() => { setSubmitError(null); setAssessmentId(null); setJobStatus(null); }}
      />
    );
  }
  if (assessmentId && !session.loggedIn) {
    return <GuestResultScreen data={data} />;
  }

  // ── Quiz wizard ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: C.sand }}>

      {/* Hero */}
      <header
        className="relative pt-20 pb-16 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${C.green} 0%, #1e4a30 100%)` }}
      >
        <div className="container mx-auto px-4 relative z-10 text-center max-w-2xl">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            Skin Assessment
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto leading-relaxed">
            Personalised skincare recommendations rooted in African botanical wisdom
          </p>
        </div>
        {/* Wave */}
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 40L1440 40V20C1200 35 960 42 720 38C480 34 240 18 0 20V40Z" fill={C.sand} />
        </svg>
      </header>

      {/* Session banner */}
      {session.loggedIn && (
        <div className="container mx-auto px-4 mt-6 max-w-2xl">
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-medium"
            style={{ background: `${C.green}12`, border: `1px solid ${C.green}30`, color: C.green }}
          >
            <User className="w-4 h-4 flex-shrink-0" />
            <span>You're logged in — your results will be saved to your member profile.</span>
          </div>
        </div>
      )}

      {/* Wizard card */}
      <main className="container mx-auto px-4 py-10 max-w-2xl">

        {/* Stepper */}
        <div className="mb-8">
          {/* Step label row */}
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.stone }}>
              Step {step} of {TOTAL_STEPS}
            </span>
            <span className="text-xs font-semibold" style={{ color: C.green }}>
              {STEP_LABELS[step - 1]}
            </span>
          </div>
          {/* Segmented progress bar */}
          <div className="flex gap-1.5">
            {STEP_LABELS.map((_, i) => {
              const n = i + 1;
              const done   = n < step;
              const active = n === step;
              return (
                <div
                  key={n}
                  className="flex-1 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    background: done
                      ? C.green
                      : active
                      ? `linear-gradient(90deg, ${C.green}, ${C.lime})`
                      : '#E5E7EB',
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Step card */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: `0 8px 48px ${C.green}12` }}
        >
          <div className="p-7 sm:p-10">
            {step === 1 && <Step1SkinType   data={data} update={update} />}
            {step === 2 && <Step2Concerns   data={data} toggle={toggleConcern} />}
            {step === 3 && <Step3Lifestyle  data={data} update={update} />}
            {step === 4 && <Step4Photos     data={data} update={update} showQR={showQR} setShowQR={setShowQR} />}
            {step === 5 && <Step5Consent    data={data} update={update} />}
          </div>

          {/* Nav bar */}
          <div
            className="px-7 sm:px-10 py-5 flex justify-between items-center border-t"
            style={{ background: '#FAFAFA', borderColor: '#F0F0F0' }}
          >
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ color: C.stone }}
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {step < TOTAL_STEPS ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-1.5 px-8 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: C.green }}
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
                className="flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: C.green }}
              >
                {submitting
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Reading between the layers…</>
                  : <><Sparkles className="w-4 h-4" /> Get My Analysis</>
                }
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {submitError && (
          <div
            className="mt-5 flex items-start gap-3 p-4 rounded-xl text-sm"
            style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {submitError}
          </div>
        )}
      </main>
    </div>
  );
}

// ─── Step 1: Skin Type ────────────────────────────────────────────────────────
function Step1SkinType({ data, update }: { data: QuizData; update: any }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold mb-1" style={{ color: C.ink }}>
          What's your skin type?
        </h2>
        <p className="text-sm" style={{ color: C.stone }}>
          Select the option that best describes your skin on a typical day
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {SKIN_TYPES.map(t => {
          const active = data.skinType === t.id;
          return (
            <button
              key={t.id}
              onClick={() => update('skinType', t.id)}
              className="flex flex-col items-center gap-2.5 p-5 rounded-2xl border-2 text-center transition-all"
              style={{
                borderColor: active ? C.green   : '#E5E7EB',
                background:  active ? `${C.green}08` : 'white',
                boxShadow:   active ? `0 4px 16px ${C.green}18` : 'none',
              }}
            >
              <span className="text-3xl">{t.emoji}</span>
              <div>
                <div className="font-semibold text-sm" style={{ color: active ? C.green : C.ink }}>
                  {t.label}
                </div>
                <div className="text-xs mt-0.5 leading-tight" style={{ color: C.stone }}>{t.desc}</div>
              </div>
              {active && (
                <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} />
              )}
            </button>
          );
        })}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: C.ink }}>
          Age range <span style={{ color: C.stone }}>(optional)</span>
        </label>
        <select
          value={data.ageRange}
          onChange={e => update('ageRange', e.target.value)}
          className="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors"
          style={{ borderColor: '#E5E7EB' }}
        >
          <option value="">Select age range</option>
          {['18–24', '25–34', '35–44', '45–54', '55+'].map(r => (
            <option key={r} value={r}>{r} years</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── Step 2: Concerns ─────────────────────────────────────────────────────────
function Step2Concerns({ data, toggle }: { data: QuizData; toggle: (c: string) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold mb-1" style={{ color: C.ink }}>
          What are your skin concerns?
        </h2>
        <p className="text-sm" style={{ color: C.stone }}>
          Select all that apply — the more you share, the better your analysis
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {CONCERNS.map(c => {
          const active = data.concerns.includes(c);
          return (
            <button
              key={c}
              onClick={() => toggle(c)}
              className="px-4 py-2 rounded-full text-sm font-medium border transition-all"
              style={{
                background:  active ? C.green : 'white',
                color:       active ? 'white' : C.ink,
                borderColor: active ? C.green : '#E5E7EB',
              }}
            >
              {c}
            </button>
          );
        })}
      </div>

      {data.concerns.length === 0 && (
        <p className="text-xs" style={{ color: C.stone }}>
          Choose at least one concern for targeted recommendations (you can skip)
        </p>
      )}
    </div>
  );
}

// ─── Step 3: Lifestyle ────────────────────────────────────────────────────────
function Step3Lifestyle({ data, update }: { data: QuizData; update: any }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold mb-1" style={{ color: C.ink }}>
          Tell us about your lifestyle
        </h2>
        <p className="text-sm" style={{ color: C.stone }}>
          Context-aware recommendations for your environment and habits
        </p>
      </div>

      {/* Sensitivity */}
      <div>
        <label className="block text-sm font-semibold mb-3" style={{ color: C.ink }}>
          Skin sensitivity level
        </label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'low',    label: 'Low',    desc: 'Rarely reacts' },
            { id: 'medium', label: 'Medium', desc: 'Sometimes reacts' },
            { id: 'high',   label: 'High',   desc: 'Often reacts' },
          ].map(s => {
            const active = data.sensitivity === s.id;
            return (
              <button
                key={s.id}
                onClick={() => update('sensitivity', s.id)}
                className="py-3 px-2 rounded-xl border-2 text-center transition-all"
                style={{
                  borderColor: active ? C.green : '#E5E7EB',
                  background:  active ? `${C.green}08` : 'white',
                }}
              >
                <div className="font-semibold text-sm" style={{ color: active ? C.green : C.ink }}>
                  {s.label}
                </div>
                <div className="text-xs mt-0.5" style={{ color: C.stone }}>{s.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Routine */}
      <div>
        <label className="block text-sm font-semibold mb-3" style={{ color: C.ink }}>
          Current skincare routine
        </label>
        <div className="space-y-2">
          {ROUTINES.map(r => {
            const active = data.routine === r.id;
            return (
              <button
                key={r.id}
                onClick={() => update('routine', r.id)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all"
                style={{
                  borderColor: active ? C.green : '#E5E7EB',
                  background:  active ? `${C.green}06` : 'white',
                }}
              >
                <div
                  className="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                  style={{ borderColor: active ? C.green : '#D1D5DB' }}
                >
                  {active && <div className="w-2 h-2 rounded-full" style={{ background: C.green }} />}
                </div>
                <span className="font-medium text-sm" style={{ color: C.ink }}>{r.label}</span>
                <span className="text-xs ml-1" style={{ color: C.stone }}>— {r.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Photos ───────────────────────────────────────────────────────────
function Step4Photos({
  data, update, showQR, setShowQR,
}: {
  data: QuizData; update: any; showQR: boolean; setShowQR: (v: boolean) => void;
}) {
  const [previews, setPreviews] = useState<Record<string, string>>({});

  const handleFile = (angle: 'front' | 'left' | 'right', file: File) => {
    update('photos', { ...data.photos, [angle]: file });
    const url = URL.createObjectURL(file);
    setPreviews(prev => ({ ...prev, [angle]: url }));
  };

  const removeFile = (angle: 'front' | 'left' | 'right') => {
    update('photos', { ...data.photos, [angle]: null });
    setPreviews(prev => { const n = { ...prev }; delete n[angle]; return n; });
  };

  const angles = [
    { id: 'front' as const, label: 'Front Face',    hint: 'Look directly at the camera',  icon: '📷' },
    { id: 'left'  as const, label: 'Left Profile',  hint: 'Turn your head to the right',  icon: '↪️' },
    { id: 'right' as const, label: 'Right Profile', hint: 'Turn your head to the left',   icon: '↩️' },
  ];

  if (showQR) {
    return (
      <div className="text-center py-6 space-y-5">
        <Smartphone className="w-12 h-12 mx-auto" style={{ color: C.green }} />
        <div>
          <h3 className="font-semibold text-lg" style={{ color: C.ink }}>Continue on your phone</h3>
          <p className="text-sm mt-1" style={{ color: C.stone }}>
            Scan the QR code to take and upload photos directly from your camera
          </p>
        </div>
        <div className="bg-white p-4 inline-block rounded-xl shadow-md border">
          <QRCodeSVG
            value={`${window.location.origin}/skin-analysis?mobile=1`}
            size={180}
            level="H"
          />
        </div>
        <button
          onClick={() => setShowQR(false)}
          className="text-sm underline"
          style={{ color: C.stone }}
        >
          Back to desktop upload
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl font-semibold mb-1" style={{ color: C.ink }}>
          Upload your photos
        </h2>
        <p className="text-sm" style={{ color: C.stone }}>
          At least one photo required · 10 MB max · JPEG, PNG or WebP
        </p>
      </div>

      <div className="space-y-3">
        {angles.map(a => (
          <div
            key={a.id}
            className="rounded-xl border-2 overflow-hidden transition-all"
            style={{
              borderColor: data.photos[a.id] ? C.green : '#E5E7EB',
              borderStyle: data.photos[a.id] ? 'solid' : 'dashed',
            }}
          >
            {data.photos[a.id] ? (
              <div className="flex items-center gap-4 p-4">
                {previews[a.id] && (
                  <img
                    src={previews[a.id]}
                    alt={a.label}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm" style={{ color: C.ink }}>{a.label}</div>
                  <div className="text-xs truncate mt-0.5" style={{ color: C.stone }}>
                    {(data.photos[a.id] as File).name}
                  </div>
                </div>
                <button
                  onClick={() => removeFile(a.id)}
                  className="p-2 rounded-lg transition-colors hover:bg-red-50"
                >
                  <X className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-4 p-4 cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={e => e.target.files?.[0] && handleFile(a.id, e.target.files[0])}
                />
                <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 bg-gray-50">
                  {a.icon}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm" style={{ color: C.ink }}>{a.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: C.stone }}>{a.hint}</div>
                </div>
                <Upload className="w-5 h-5 flex-shrink-0" style={{ color: C.stone }} />
              </label>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div
          className="flex-1 px-4 py-3 rounded-xl text-sm leading-relaxed"
          style={{ background: `${C.lime}18` }}
        >
          <strong style={{ color: C.green }}>Tip: </strong>
          <span style={{ color: C.stone }}>
            Natural light, no makeup, full face visible gives the most accurate analysis
          </span>
        </div>
        <button
          onClick={() => setShowQR(true)}
          className="flex items-center gap-2 text-sm px-4 py-3 rounded-xl border transition-colors flex-shrink-0"
          style={{ borderColor: '#E5E7EB', color: C.stone }}
        >
          <Smartphone className="w-4 h-4" /> Use phone camera
        </button>
      </div>
    </div>
  );
}

// ─── Step 5: Consent ──────────────────────────────────────────────────────────
function Step5Consent({ data, update }: { data: QuizData; update: any }) {
  const items = [
    {
      id:    'analysis' as const,
      title: 'Skin Analysis Consent *',
      desc:  'I consent to Marple & Co analysing my skin profile and photos to provide personalised recommendations. I understand this is not a medical diagnosis.',
    },
    {
      id:    'photoRetention' as const,
      title: 'Photo Retention (Optional)',
      desc:  'I consent to storing my photos for progress comparison and tracking. You can request deletion at any time.',
    },
    {
      id:    'marketing' as const,
      title: 'Marketing Communications (Optional)',
      desc:  'I consent to receiving personalised skincare tips, product recommendations, and exclusive offers by email.',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold mb-1" style={{ color: C.ink }}>
          Consent & Privacy
        </h2>
        <p className="text-sm" style={{ color: C.stone }}>
          Please review and agree before we run your analysis
        </p>
      </div>

      <div className="space-y-3">
        {items.map(item => {
          const checked = data.consent[item.id];
          return (
            <label
              key={item.id}
              className="flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all"
              style={{
                borderColor: checked ? C.green : '#E5E7EB',
                background:  checked ? `${C.green}06` : 'white',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={e => update('consent', { ...data.consent, [item.id]: e.target.checked })}
                className="mt-0.5 w-5 h-5 rounded flex-shrink-0"
                style={{ accentColor: C.green }}
              />
              <div>
                <div className="font-semibold text-sm" style={{ color: C.ink }}>{item.title}</div>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: C.stone }}>{item.desc}</p>
              </div>
            </label>
          );
        })}
      </div>

      <p className="text-xs" style={{ color: C.stone }}>
        Your data is processed in accordance with POPIA.{' '}
        <Link to="/privacy" className="underline" style={{ color: C.green }}>
          Privacy Policy
        </Link>
      </p>
    </div>
  );
}

// ─── Processing screen (member, waiting for AI) ───────────────────────────────

// ─── Guest result screen ──────────────────────────────────────────────────────
function GuestResultScreen({ data }: { data: QuizData }) {
  return (
    <div className="min-h-screen" style={{ background: C.sand }}>
      <div className="container mx-auto px-4 py-20 max-w-2xl">
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: `0 8px 48px ${C.green}12` }}
        >
          {/* Header */}
          <div
            className="p-8 text-center"
            style={{ background: `linear-gradient(135deg, ${C.green} 0%, #1e4a30 100%)` }}
          >
            <CheckCircle2 className="w-14 h-14 mx-auto text-white mb-4" />
            <h2 className="font-serif text-3xl font-bold text-white mb-2">
              Preliminary Analysis Ready
            </h2>
            <p className="text-white/70">Based on your quiz responses</p>
          </div>

          <div className="p-8 space-y-6">
            {/* Summary */}
            <div
              className="p-5 rounded-xl space-y-3 text-sm"
              style={{ background: `${C.lime}18` }}
            >
              <h3 className="font-semibold" style={{ color: C.ink }}>Your skin profile</h3>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span style={{ color: C.stone }}>Skin type: </span>
                  <strong style={{ color: C.ink }}>{data.skinType || '—'}</strong>
                </div>
                <div>
                  <span style={{ color: C.stone }}>Sensitivity: </span>
                  <strong style={{ color: C.ink }}>{data.sensitivity}</strong>
                </div>
              </div>
              {data.concerns.length > 0 && (
                <div>
                  <span style={{ color: C.stone }}>Top concerns: </span>
                  <strong style={{ color: C.ink }}>
                    {data.concerns.slice(0, 3).join(', ')}
                    {data.concerns.length > 3 ? ` +${data.concerns.length - 3} more` : ''}
                  </strong>
                </div>
              )}
            </div>

            {/* Upgrade CTA */}
            <div
              className="rounded-xl border-2 p-6 text-center space-y-4"
              style={{ borderColor: `${C.green}30`, background: `${C.green}04` }}
            >
              <Sparkles className="w-10 h-10 mx-auto" style={{ color: C.green }} />
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: C.ink }}>
                  Unlock your full AI analysis
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.stone }}>
                  Create a free account to get a detailed AI-powered assessment with personalised
                  routine, ingredient guide, and product recommendations — powered by advanced AI.
                </p>
              </div>
              <Link
                to="/member/create-account"
                className="inline-flex items-center justify-center w-full py-3.5 rounded-xl font-semibold text-white transition-all"
                style={{ background: C.green }}
              >
                Create Free Account
              </Link>
              <Link
                to="/member/login"
                className="block text-sm underline"
                style={{ color: C.stone }}
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SkinQuiz;
